import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from "@material-ui/core/styles";
import InputAdornment from "@material-ui/core/InputAdornment";
import Icon from "@material-ui/core/Icon";
import Header from "components/Header/Header.js";
import HeaderLinks from "components/Header/HeaderLinks.js";
import Footer from "components/Footer/Footer.js";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import CardFooter from "components/Card/CardFooter.js";
import CustomInput from "components/CustomInput/CustomInput.js";
import styles from "assets/jss/material-kit-react/views/loginPage.js";
import image from "assets/img/background.jpg";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from 'react-router-dom';
import PhoneIcon from '@material-ui/icons/Phone';
import PeopleIcon from '@material-ui/icons/People';
import EmailIcon from '@material-ui/icons/Email';
import VisibilityIcon from '@material-ui/icons/Visibility';
import AlertDialog from '../components/AlertDialog';
import CountrySelect from '../components/CountrySelect';
import { api, FirebaseContext } from 'common';
import { useTranslation } from "react-i18next";
import { GoogleLogin } from '@react-oauth/google';
import {
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip,
  OutlinedInput,
  FormControl,
  InputLabel,
  Box,
  CircularProgress,
  DialogActions,
  Typography
} from '@mui/material';
import { colors } from '../components/Theme/WebTheme';
import { MAIN_COLOR } from "../common/sharedFunctions";

const useStyles = makeStyles(theme => ({
  ...styles,
  selectField: {
    color: "black",
  },
}));

const EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const validatePassword = (pass, t) => {
  if (pass !== undefined && pass !== null) {
    if (!/^\S*$/.test(pass)) return t('password_must_not_contain_whitespaces');
    if (!/^(?=.*[A-Z]).*$/.test(pass)) return t('password_must_have_at_least_one_uppercase_character');
    if (!/^(?=.*[a-z]).*$/.test(pass)) return t('password_must_have_at_least_one_lowercase_character');
    if (!/^(?=.*[0-9]).*$/.test(pass)) return t('password_must_contain_at_least_one_digit');
    if (!/^.{8,16}$/.test(pass)) return t('password_must_be_8-16_characters_long');
  }
  return null;
};

export default function LoginPage(props) {
  const { authRef, RecaptchaVerifier, signInWithPhoneNumber } = useContext(FirebaseContext);
  const { t } = useTranslation();
  const {
    googleLogin,
    clearLoginError,
    mobileSignIn,
    countries,
    sendResetMail,
    verifyEmailPassword,
    requestMobileOtp,
    verifyMobileOtp,
    mainSignUp,
    checkUserExists,
    validateReferer
  } = api;

  const navigate = useNavigate();
  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settingsdata.settings);
  const dispatch = useDispatch();

  const [capatchaReady, setCapatchaReady] = React.useState(false);
  const [loading, setLoading] = useState(false);
  const [appVerifier, setAppVerifier] = useState(null);
  const [mobileWithoutCountry, setMobileWithoutCountry] = useState('');
  const [showSignUp, setShowSignUp] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otpPending, setOtpPending] = useState();
  const [commonAlert, setCommonAlert] = useState({ open: false, msg: '' });

  const [data, setData] = React.useState({
    email: '',
    confirmpassword: '',
    country: '',
    contact: '',
    mobile: '',
    password: '',
    otp: '',
    verificationId: null,
    firstName: '',
    lastName: '',
    selectedcountry: null,
    usertype: ['customer'],
    referralId: '',
    entryType: null
  });

  const classes = useStyles();
  const { ...rest } = props;

  useEffect(() => {
    if (settings) {
      for (let i = 0; i < countries.length; i++) {
        if (countries[i].label === settings.country) {
          setData({ country: countries[i].phone, selectedcountry: countries[i] });
        }
      }
    }
  }, [settings, countries]);

  useEffect(() => {
    if (!window.recaptchaVerifier) {
      setCapatchaReady(true);
    }
    if (auth.profile && auth.profile.uid) {
      let role = auth.profile.usertype;
      navigate(role === 'admin' || role === 'fleetadmin' ? '/dashboard' : role === 'driver' ? '/bookings' : '/');
    }
    if (auth.error && auth.error.flag && auth.error.msg.message !== t('not_logged_in')) {
      if (auth.error.msg.message === t('require_approval')) {
        setCommonAlert({ open: true, msg: t('require_approval') });
      } else if (
        auth.error.msg.message === 'Firebase: Error (auth/invalid-verification-code).' ||
        (data.contact && (data.contact === '' || data.contact))
      ) {
        setCommonAlert({ open: true, msg: t('login_error') });
      }
    }
    if (auth.verificationId && otpPending) {
      setOtpPending(false);
      setData({ ...data, verificationId: auth.verificationId });
    }
  }, [auth.profile, auth.error, auth.verificationId, navigate, data, data.email, data.contact, capatchaReady, RecaptchaVerifier, t, otpPending]);

  const handleGoogleResponse = (res) => {
    if (res && res.credential) {
      dispatch(googleLogin(res.credential, null));
    } else {
      setCommonAlert({ open: true, msg: t('login_error') });
      setLoading(false);
    }
  };

  const onInputChange = (event) => {
    setData({ ...data, [event.target.id]: event.target.value });
  };

  const onCountryChange = (object, value) => {
    if (value && value.phone) {
      setData({ ...data, country: value.phone, selectedcountry: value });
      setMobileWithoutCountry('');
    }
  };

  const handleCommonAlertClose = (e) => {
    e.preventDefault();
    setCommonAlert({ open: false, msg: '' });
    if (auth.error.flag) {
      setData({ ...data, email: '', pass: '' });
      dispatch(clearLoginError());
    }
  };

  const handleSignUp = (e) => {
    e.preventDefault();
    new Promise((resolve, reject) => {
      setLoading(true);
      const passwordError = data.password !== data.confirmpassword
        ? t('confirm_password_not_match_err')
        : validatePassword(data.password, t);

      if (!EMAIL_REGEX.test(data.email)) {
        setCommonAlert({ open: true, msg: t('proper_email') });
        reject();
        return;
      }
      if (!(/\S/.test(data.firstName) && data.firstName.length > 0 && /\S/.test(data.lastName) && data.lastName.length > 0)) {
        setCommonAlert({ open: true, msg: t('proper_input_name') });
        reject();
        return;
      }
      if (passwordError) {
        setCommonAlert({ open: true, msg: passwordError });
        reject();
        return;
      }
      if (mobileWithoutCountry.length < 6) {
        setCommonAlert({ open: true, msg: t('mobile_no_blank_error') });
        reject();
        return;
      }

      const regData = { ...data };
      if (regData.usertype.includes('customer')) delete regData.carType;
      delete regData.confirmpassword;
      delete regData.pass;

      const onCreated = (res) => {
        if (res.uid) {
          setData({
            ...data,
            email: '', firstName: '', lastName: '', password: '',
            confirmpassword: '', usertype: ['customer'], referralId: ''
          });
          setMobileWithoutCountry('');
          setAccountCreated(true);
          setLoading(false);
          resolve();
        } else {
          setCommonAlert({ open: true, msg: t('reg_error') });
          setLoading(false);
        }
      };

      checkUserExists(regData).then((res) => {
        if (res.users && res.users.length > 0) {
          setCommonAlert({ open: true, msg: t('user_exists') });
          setLoading(false);
        } else if (regData.referralId && regData.referralId.length > 0) {
          validateReferer(regData.referralId).then((referer) => {
            if (referer.uid) {
              mainSignUp({ ...regData, signupViaReferral: referer.uid }).then(onCreated);
            } else {
              setCommonAlert({ open: true, msg: t('referer_not_found') });
              setLoading(false);
            }
          }).catch(() => {
            setCommonAlert({ open: true, msg: t('referer_not_found') });
            setLoading(false);
          });
        } else {
          mainSignUp(regData).then(onCreated);
        }
      });
    }).catch(() => { }).finally(() => { setLoading(false); });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!data.country) {
      setCommonAlert({ open: true, msg: t('country_blank_error') });
      setLoading(false);
      return;
    }
    if (!data.contact) {
      setCommonAlert({ open: true, msg: t('contact_input_error') });
      setLoading(false);
      return;
    }
    if (isNaN(data.contact)) {
      setData({ ...data, entryType: 'email' });
      if (EMAIL_REGEX.test(data.contact)) {
        await dispatch(verifyEmailPassword(data.contact, data.otp));
      } else {
        setCommonAlert({ open: true, msg: t('proper_email') });
        setLoading(false);
      }
    } else {
      setData({ ...data, entryType: 'mobile' });
      let mobile = data.contact.replace(/ /g, '');
      const phoneNumber = "+" + data.country + mobile;
      if (mobile.length > 6) {
        if (settings.customMobileOTP) {
          dispatch(requestMobileOtp(phoneNumber));
          setData({ ...data, verificationId: true });
          setLoading(false);
        } else {
          if (!window.recaptchaVerifier || appVerifier === null) {
            window.recaptchaVerifier = await new RecaptchaVerifier(authRef(), "sign-in-button", {
              size: 'invisible',
              callback: function (response) { setCapatchaReady(true); }
            });
          }
          const verifier = window.recaptchaVerifier;
          setAppVerifier(verifier);
          await signInWithPhoneNumber(authRef(), phoneNumber, verifier).then((res) => {
            setData({ ...data, verificationId: res.verificationId });
            setLoading(false);
            window.recaptchaVerifier.clear();
          }).catch((error) => {
            setCommonAlert({ open: true, msg: error.code + ": " + error.message });
            setLoading(false);
          });
        }
      } else {
        setCommonAlert({ open: true, msg: t('mobile_no_blank_error') });
        setLoading(false);
      }
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (data.otp && data.otp.length === 6) {
      if (settings.customMobileOTP) {
        let mobile = data.contact.replace(/ /g, '');
        const phoneNumber = "+" + data.country + mobile;
        dispatch(verifyMobileOtp(phoneNumber, data.otp));
      } else {
        await dispatch(mobileSignIn(data.verificationId, data.otp));
      }
      setLoading(false);
    } else {
      setCommonAlert({ open: true, msg: t('otp_validate_error') });
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (EMAIL_REGEX.test(data.contact)) {
      await dispatch(sendResetMail(data.contact));
      setCommonAlert({ open: true, msg: t('email_send') });
    } else {
      setCommonAlert({ open: true, msg: t('proper_email') });
    }
  };

  const handleUserTypeChange = (event) => {
    const value = event.target.value;
    let next = typeof value === 'string' ? value.split(',') : value;
    if (next.includes('agent') && !next.includes('customer')) next.push('customer');
    if (next.includes('agent')) {
      setData({ ...data, usertype: next, referralId: '' });
    } else {
      setData({ ...data, usertype: next });
    }
  };

  const peopleAdornment = (
    <InputAdornment position="start">
      <PeopleIcon className={classes.inputIconsColor} />
    </InputAdornment>
  );

  return (
    <div>
      <Header
        absolute
        color="transparent"
        rightLinks={<HeaderLinks />}
        {...rest}
      />
      <div
        className={classes.pageHeader}
        style={{
          backgroundImage: "url(" + image + ")",
          backgroundSize: "cover",
          backgroundPosition: "top center",
          display: "flex"
        }}
      >
        {showSignUp ?
          <div className={classes.container}>
            <GridContainer justifyContent="center">
              <GridItem xs={12} sm={12} md={8}>
                <Card>
                  <form className={classes.form}>
                    <CardHeader style={{ backgroundColor: MAIN_COLOR, borderRadius: 10 }} className={classes.cardHeader}>
                      <h4 style={{ color: colors.WHITE, fontSize: 20 }}>{t('signup')}</h4>
                    </CardHeader>
                    <CardBody>
                      <GridContainer spacing={2} sx={{ display: 'flex', gridTemplateColumns: '50%' }}>
                        <GridItem xs={12} sm={12} md={12} lg={6} xl={6}>
                          <CustomInput
                            labelText={t('first_name')}
                            id="firstName"
                            formControlProps={{ fullWidth: true }}
                            inputProps={{ required: true, endAdornment: peopleAdornment }}
                            onChange={onInputChange}
                            value={data.firstName}
                          />
                        </GridItem>
                        <GridItem xs={12} sm={12} md={12} lg={6} xl={6}>
                          <CustomInput
                            labelText={t('last_name')}
                            id="lastName"
                            formControlProps={{ fullWidth: true }}
                            inputProps={{ required: true, endAdornment: peopleAdornment }}
                            onChange={onInputChange}
                            value={data.lastName}
                          />
                        </GridItem>
                        <GridItem xs={12} sm={12} md={12} lg={6} xl={6}>
                          {data.selectedcountry && settings.mobileLogin && countries && countries.length > 0 ?
                            <CountrySelect
                              countries={countries}
                              label={t('select_country')}
                              value={data.selectedcountry}
                              onChange={onCountryChange}
                              style={{ paddingTop: 20 }}
                              disabled={data.verificationId ? true : false}
                            />
                            : null}
                        </GridItem>
                        <GridItem xs={12} sm={12} md={12} lg={6} xl={6}>
                          <CustomInput
                            labelText={t('phone')}
                            id="mobileWithoutCountry"
                            formControlProps={{ fullWidth: true }}
                            inputProps={{
                              required: true,
                              endAdornment: (
                                <InputAdornment position="start">
                                  <PhoneIcon className={classes.inputIconsColor} />
                                </InputAdornment>
                              )
                            }}
                            onChange={(e) => {
                              const value = e.target.value;
                              setMobileWithoutCountry(value);
                              let mobile = value.replace(/ /g, '');
                              setData({ ...data, mobile: "+" + data.country + mobile });
                            }}
                            value={mobileWithoutCountry}
                          />
                        </GridItem>
                        <GridItem xs={12} sm={12} md={12} lg={6} xl={6} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <FormControl variant="standard" fullWidth>
                            <InputLabel id="user-type-label">{t('User Type')}</InputLabel>
                            <Select
                              labelId="user-type-label"
                              id="user-type-select"
                              multiple
                              value={data.usertype || []}
                              onChange={handleUserTypeChange}
                              input={<OutlinedInput label={t('User Type')} />}
                              renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {selected.map((value) => <Chip key={value} label={value} />)}
                                </Box>
                              )}
                              className={classes.selectField}
                              aria-label="userType"
                            >
                              <MenuItem value="customer">
                                <Checkbox checked={data.usertype.indexOf('customer') > -1} />
                                <ListItemText primary="Customer" />
                              </MenuItem>
                              <MenuItem value="driver">
                                <Checkbox checked={data.usertype.indexOf('driver') > -1} />
                                <ListItemText primary="Driver" />
                              </MenuItem>
                              <MenuItem value="agent">
                                <Checkbox checked={data.usertype.indexOf('agent') > -1} />
                                <ListItemText primary="Agent" />
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </GridItem>
                        <GridItem xs={12} sm={12} md={12} lg={6} xl={6}>
                          <CustomInput
                            labelText={t('email')}
                            id="email"
                            formControlProps={{ fullWidth: true }}
                            inputProps={{
                              required: true,
                              endAdornment: (
                                <InputAdornment position="start">
                                  <EmailIcon className={classes.inputIconsColor} />
                                </InputAdornment>
                              )
                            }}
                            onChange={onInputChange}
                            value={data.email}
                          />
                        </GridItem>
                        <GridItem xs={12} sm={12} md={12} lg={6} xl={6}>
                          <CustomInput
                            labelText={t('password')}
                            id="password"
                            formControlProps={{ fullWidth: true }}
                            inputProps={{
                              required: true,
                              type: showPassword ? "text" : "password",
                              endAdornment: (
                                <InputAdornment position="start">
                                  <VisibilityIcon className={classes.inputIconsColor} onClick={() => setShowPassword(!showPassword)} />
                                </InputAdornment>
                              )
                            }}
                            onChange={onInputChange}
                            value={data.password}
                          />
                        </GridItem>
                        <GridItem xs={12} sm={12} md={12} lg={6} xl={6}>
                          <CustomInput
                            labelText={t('confirm_password')}
                            id="confirmpassword"
                            formControlProps={{ fullWidth: true }}
                            inputProps={{
                              required: true,
                              type: showConfirmPassword ? "text" : "password",
                              endAdornment: (
                                <InputAdornment position="start">
                                  <VisibilityIcon className={classes.inputIconsColor} onClick={() => setShowConfirmPassword(!showConfirmPassword)} />
                                </InputAdornment>
                              )
                            }}
                            onChange={onInputChange}
                            value={data.confirmpassword}
                          />
                        </GridItem>
                        {!data.usertype.includes('agent') &&
                          <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
                            <CustomInput
                              labelText={t('referralId')}
                              id="referralId"
                              formControlProps={{ fullWidth: true }}
                              inputProps={{ endAdornment: peopleAdornment }}
                              onChange={onInputChange}
                              value={data.referralId}
                            />
                          </GridItem>
                        }
                      </GridContainer>
                    </CardBody>
                    <CardFooter className={classes.cardFooter}>
                      {accountCreated ?
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                          <Typography style={{ color: colors.GREEN, fontWeight: 'bold' }}>
                            {t('account_create_successfully')}
                          </Typography>
                          <Button className={classes.normalButton} simple color="info" onClick={() => setShowSignUp(false)}>
                            {t('signIn')}
                          </Button>
                        </div>
                        : loading ?
                          <DialogActions style={{ justifyContent: 'center', alignContent: 'center' }}>
                            <CircularProgress />
                          </DialogActions>
                          :
                          <>
                            <Button className={classes.normalButton} simple color="info" size="lg" type="submit" onClick={handleSignUp}>
                              {t('signup')}
                            </Button>
                            <Button
                              className={classes.normalButton}
                              simple
                              color="info"
                              size="lg"
                              onClick={() => {
                                setData({
                                  ...data, email: '', firstName: '', lastName: '',
                                  password: '', confirmpassword: '', usertype: 'customer', referralId: ''
                                });
                                setMobileWithoutCountry('');
                                setShowSignUp(!showSignUp);
                              }}
                            >
                              {t('login')}
                            </Button>
                          </>
                      }
                    </CardFooter>
                  </form>
                </Card>
              </GridItem>
            </GridContainer>
          </div>
          :
          <>
            <div id="sign-in-button" />
            <div className={classes.container}>
              <GridContainer justifyContent="center">
                <GridItem xs={12} sm={12} md={4}>
                  <Card>
                    <form className={classes.form}>
                      {settings && settings.socialLogin ?
                        <CardHeader style={{ backgroundColor: MAIN_COLOR, borderRadius: 10 }} className={classes.cardHeader}>
                          <h4 style={{ color: colors.WHITE, fontSize: 20 }}>{t('login')}</h4>
                          <div className={classes.socialLine} style={{ alignItems: 'center', justifyContent: 'center', alignContent: 'center' }}>
                            <div className={classes.socialLine}>
                              <Button justIcon href="#pablo" target="_blank" color="transparent">
                                <GoogleLogin onSuccess={handleGoogleResponse} onError={handleGoogleResponse} />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        : null}
                      <CardBody>
                        {data.selectedcountry && settings.mobileLogin && countries && countries.length > 0 ?
                          <CountrySelect
                            countries={countries}
                            label={t('select_country')}
                            value={data.selectedcountry}
                            onChange={onCountryChange}
                            style={{ paddingTop: 20 }}
                            disabled={data.verificationId ? true : false}
                          />
                          : null}
                        <CustomInput
                          labelText={
                            settings.emailLogin && settings.mobileLogin ? t('contact_placeholder')
                              : settings.emailLogin && !settings.mobileLogin ? t('email_id')
                                : t('mobile_number')
                          }
                          id="contact"
                          formControlProps={{ fullWidth: true }}
                          inputProps={{
                            required: true,
                            disabled: data.verificationId ? true : false,
                            endAdornment: peopleAdornment
                          }}
                          onChange={onInputChange}
                          value={data.contact}
                        />
                        {(data.contact && isNaN(data.contact)) || (settings.emailLogin && !settings.mobileLogin) ?
                          <CustomInput
                            labelText={(data.contact && isNaN(data.contact)) || (settings.emailLogin && !settings.mobileLogin) ? t('password') : t('otp_here')}
                            id="otp"
                            formControlProps={{ fullWidth: true }}
                            inputProps={{
                              type: "password",
                              required: true,
                              endAdornment: (
                                <InputAdornment position="start">
                                  <Icon className={classes.inputIconsColor}>lock_outline</Icon>
                                </InputAdornment>
                              ),
                              autoComplete: "off"
                            }}
                            onChange={onInputChange}
                            value={data.otp}
                          />
                          : null}
                        {data.verificationId ?
                          <CustomInput
                            labelText={t('otp')}
                            id="otp"
                            formControlProps={{ fullWidth: true }}
                            inputProps={{
                              type: "password",
                              required: true,
                              endAdornment: (
                                <InputAdornment position="start">
                                  <Icon className={classes.inputIconsColor}>lock_outline</Icon>
                                </InputAdornment>
                              ),
                              autoComplete: "off"
                            }}
                            onChange={onInputChange}
                            value={data.otp}
                          />
                          : null}
                      </CardBody>
                      <CardFooter className={classes.cardFooter}>
                        {loading ?
                          <DialogActions style={{ justifyContent: 'center', alignContent: 'center' }}>
                            <CircularProgress />
                          </DialogActions>
                          : data.verificationId ?
                            <div>
                              <Button className={classes.normalButton} simple color="primary" size="lg" type="submit" onClick={handleVerifyOTP}>
                                {t('verify_otp')}
                              </Button>
                              <Button
                                className={classes.normalButton}
                                simple
                                color="primary"
                                size="lg"
                                onClick={() => { setData({ ...data, contact: null, verificationId: null }); setLoading(false); }}
                              >
                                {t('cancel')}
                              </Button>
                            </div>
                            :
                            <div>
                              <Button className={classes.normalButton} simple color="primary" size="lg" type="submit" onClick={handleLogin}>
                                {data.contact && isNaN(data.contact) ? t('login') : t('login_otp')}
                              </Button>
                              {data.contact && isNaN(data.contact) ?
                                <Button className={classes.normalButton} simple color="primary" size="lg" onClick={handleForgotPassword}>
                                  {t('forgot_password')}
                                </Button>
                                : null}
                            </div>
                        }
                      </CardFooter>
                    </form>
                  </Card>
                </GridItem>
              </GridContainer>
            </div>
          </>
        }
        <Footer whiteFont />
        <AlertDialog open={commonAlert.open} onClose={handleCommonAlertClose}>{commonAlert.msg}</AlertDialog>
      </div>
    </div>
  );
}
